import { useState } from 'react';
import { NavBar } from '../components/NavBar.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { Alert, Sheet } from '../components/Overlay.tsx';
import { Icon } from '../components/Icon.tsx';
import { ErrorState, Loading } from '../components/DataState.tsx';
import { Stub } from '../components/Stub.tsx';
import { ApiError } from '../api/client.ts';
import { patchFriend, removeFriend, useFriends } from '../api/rooms.ts';
import type { Friend, Person } from '../domain.ts';
import { FriendPopup } from './partner/FriendPopup.tsx';
import { InviteSheet } from './partner/InviteSheet.tsx';

/**
 * D1 ~ D3 — Prayer Partner.
 *
 * 리스트에 보이는 것은 기획서 2.1 대로 사진과 이름뿐입니다. 누르면 팝업(D2),
 * 길게 누르면 관리 시트(D3)입니다.
 *
 * **고정한 친구가 위로 옵니다** (D3 의 「리스트 상단에 고정」). 그 안에서는
 * 이름순입니다 — 친구가 늘어도 같은 사람이 같은 자리에 있어야 찾기 쉽습니다.
 *
 * 차단한 상대도 목록에 남습니다. D3 에서 풀 수 있어야 하기 때문입니다 —
 * 목록에서 사라지면 풀 자리가 없어집니다.
 *
 * 친구를 **모르는 사람 중에서 찾는 길은 아직 없습니다.** 초대 코드인지
 * 닉네임 검색인지 카카오 링크인지가 정해지지 않았습니다. 지금 친구가 되는
 * 길은 모임에서 만난 구성원을 팝업에서 추가하는 것(D2-1) 하나입니다.
 */
export function Partner() {
  const { data: friends, loading, offline, error, reload } = useFriends();
  const [popup, setPopup] = useState<Person | null>(null);
  const [manage, setManage] = useState<Friend | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [invite, setInvite] = useState(false);
  /** 차단하기 전에 «가 있는 노트를 어떻게 할까»를 묻습니다 (7절) */
  const [blocking, setBlocking] = useState<Friend | null>(null);

  if (offline) {
    return (
      <div className="screen">
        <NavBar title="Prayer Partner" large />
        <div className="screen__body">
          <Stub
            icon="userPlus"
            title="로그인이 필요합니다"
            note="친구 목록은 누가 누구의 친구인지를 서버가 들고 있어 목업으로 보여드릴 수 없습니다. 앱에서 카카오로 로그인하면 열립니다."
            screens="D1 ~ D3"
          />
        </div>
      </div>
    );
  }

  const list = [...(friends ?? [])].sort(
    (a, b) =>
      Number(b.pinned) - Number(a.pinned) || (a.name ?? '').localeCompare(b.name ?? ''),
  );
  const pinned = list.filter((f) => f.pinned);
  const rest = list.filter((f) => !f.pinned);

  /** 서버가 거절하면 그 문장을 그대로 띄웁니다 */
  const apply = async (run: () => Promise<unknown>) => {
    setFailure(null);
    try {
      await run();
      reload();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '바꾸지 못했습니다.');
    }
  };

  const row = (f: Friend) => (
    <button
      key={f.userId}
      type="button"
      className="row"
      onClick={() => setPopup(f)}
      onContextMenu={(e) => {
        e.preventDefault();
        setManage(f);
      }}
    >
      <Avatar name={f.name ?? '?'} seed={f.userId} />
      <span className="row__grow">
        <span className="row__name">{f.name ?? '이름 없음'}</span>
        {/*
          부제에 대표 개수를 적지 않습니다. 목록에서 개수를 보여 주려면 친구
          수만큼 노트를 끌고 와야 하고, 그 값이 목록에서 쓰이지도 않습니다.
          차단은 반드시 보여야 합니다 — 왜 대표가 안 보이는지의 답입니다.
        */}
        <span className="row__sub">
          {f.state === 'BLOCKED' ? '차단한 친구' : f.mutual ? '서로 친구' : '상대의 확인을 기다립니다'}
        </span>
      </span>
      {f.pinned && (
        <span className="row__star">
          <Icon name="star" size={18} />
        </span>
      )}
    </button>
  );

  return (
    <div className="screen">
      {/*
        우상단 ＋ 가 초대 코드(D1-1)를 엽니다. 모르는 사람을 찾는 방식이
        정해지기 전에는 비워 두었던 자리입니다.
      */}
      <NavBar
        title="Prayer Partner"
        large
        end={{ icon: 'userPlus', label: '친구 추가', accent: true, onClick: () => setInvite(true) }}
      />

      <div className="screen__body">
        {loading && <Loading label="친구를 불러오는 중" />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        {!loading && list.length === 0 && (
          <div className="empty">
            <p className="empty__title">아직 친구가 없습니다</p>
            <p className="empty__hint">
              오른쪽 위 ＋ 로 초대 코드를 주고받으면 친구가 됩니다. 모임에서 만난
              구성원은 이름을 눌러 바로 추가할 수도 있습니다.
            </p>
          </div>
        )}

        {pinned.length > 0 && (
          <>
            <p className="section-title">
              고정 <span>{pinned.length}</span>
            </p>
            <div className="list">{pinned.map(row)}</div>
          </>
        )}

        {rest.length > 0 && (
          <>
            <p className="section-title">
              전체 <span>{list.length}</span>
            </p>
            <div className="list">{rest.map(row)}</div>
          </>
        )}
      </div>

      {popup && (
        <FriendPopup
          person={popup}
          onClose={() => setPopup(null)}
          onManage={(person) => {
            setManage(list.find((f) => f.userId === person.userId) ?? null);
            setPopup(null);
          }}
          onAdded={reload}
        />
      )}

      {manage && (
        <Sheet onClose={() => setManage(null)}>
          <div className="sheet__person">
            <Avatar name={manage.name ?? '?'} seed={manage.userId} />
            <div>
              <p className="row__name">{manage.name ?? '이름 없음'}</p>
              <p className="row__sub">
                {manage.state === 'BLOCKED' ? '차단한 친구' : '서로 친구'}
              </p>
            </div>
          </div>

          {/* 차단과 삭제는 결과가 다릅니다. 무슨 일이 생기는지 각 행에 적습니다 */}
          <button
            type="button"
            className="mrow"
            onClick={() => {
              const next = !manage.pinned;
              setManage(null);
              void apply(() => patchFriend(manage.userId, { pinned: next }));
            }}
          >
            <Icon name="star" size={20} />
            <span className="mrow__grow">
              {manage.pinned ? '고정에서 빼기' : '리스트 상단에 고정'}
              <span className="mrow__sub">목록 맨 위에 모아 둡니다</span>
            </span>
          </button>

          <button
            type="button"
            className="mrow"
            onClick={() => {
              const target = manage;
              setManage(null);
              // 푸는 것은 바로. 거는 것은 가 있는 노트를 어떻게 할지 묻습니다
              if (target.state === 'BLOCKED') {
                void apply(() => patchFriend(target.userId, { state: 'ACTIVE' }));
              } else {
                setBlocking(target);
              }
            }}
          >
            <Icon name="block" size={20} />
            <span className="mrow__grow">
              {manage.state === 'BLOCKED' ? '차단 풀기' : '차단'}
              <span className="mrow__sub">
                {manage.state === 'BLOCKED'
                  ? '다시 서로의 대표 기도제목이 보입니다'
                  : '서로의 대표 기도제목이 보이지 않게 됩니다'}
              </span>
            </span>
          </button>

          <button
            type="button"
            className="mrow mrow--danger"
            onClick={() => {
              setManage(null);
              void apply(() => removeFriend(manage.userId));
            }}
          >
            <Icon name="trash" size={20} />
            <span className="mrow__grow">
              친구 삭제
              <span className="mrow__sub">함께 있던 모임에서는 나가지 않습니다</span>
            </span>
          </button>

          <button type="button" className="btn btn--secondary" onClick={() => setManage(null)}>
            취소
          </button>
        </Sheet>
      )}

      {invite && <InviteSheet onClose={() => setInvite(false)} onAdded={reload} />}

      {/*
        차단하면 이미 가 있는 노트를 어떻게 할지 묻습니다 (screens.md 7절).
        **이모티콘과 코멘트는 선택과 무관하게 전부 사라집니다** — 서로의
        화면에서 상대가 지워지는 일이라, 남겨 두면 이름 없는 말이 남습니다.
      */}
      {blocking && (
        <Alert
          title={`${blocking.name ?? '이 친구'}를 차단할까요?`}
          onClose={() => setBlocking(null)}
          actions={
            <>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => {
                  const target = blocking;
                  setBlocking(null);
                  void apply(() =>
                    patchFriend(target.userId, { state: 'BLOCKED', revokeShared: false }),
                  );
                }}
              >
                남겨 두기
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => {
                  const target = blocking;
                  setBlocking(null);
                  void apply(() =>
                    patchFriend(target.userId, { state: 'BLOCKED', revokeShared: true }),
                  );
                }}
              >
                회수하기
              </button>
            </>
          }
        >
          <p>서로의 대표 기도제목이 보이지 않게 됩니다.</p>
          <p>
            <b>이미 보낸 기도 노트</b>를 거둘지 고르세요. 남겨 두면 그 사람에게는 계속
            보입니다.
          </p>
          <p className="alert__note">
            주고받은 이모티콘과 코멘트는 어느 쪽을 고르든 모두 사라집니다.
          </p>
        </Alert>
      )}
    </div>
  );
}
